/**
 * PermissionGuard — Phase 5.
 *
 * Route-level guard for tenant-owned pages that require specific
 * permissions. Sits inside the protected guard chain:
 *
 *   ProtectedRoute → BusinessGuard → AppLayout → PermissionGuard → page
 *
 * By the time PermissionGuard runs we already know the user is
 * authenticated AND tenant-ready. So denied means denied — never an
 * auth failure. PermissionGuard renders an inline denied state and
 * does NOT navigate to /login, NOT clear auth state, NOT touch the
 * query cache. Backend authorization is still authoritative; this
 * guard exists so direct URL navigation matches the API's truth and
 * the user gets a clean message instead of a flash-of-broken-page.
 *
 * Three call shapes — pick exactly one per usage:
 *
 *   <PermissionGuard required="product:read"> ... </PermissionGuard>
 *   <PermissionGuard requiredAny={['order:create', 'order:read']}> ... </>
 *   <PermissionGuard requiredAll={['product:update', 'product:delete']}> ... </>
 *
 * Mixing call shapes is a programmer error and surfaces as a typed
 * union, not a runtime branch.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Permission } from '@invento/shared';
import { Button } from '@invento/ui/components/button';
import { PermissionDeniedState as SharedPermissionDeniedState } from '@invento/ui/components/permission-denied-state';
import { ROUTE_PATHS } from '@/navigation';
import {
  useHasPermission,
  useHasAnyPermission,
  useHasAllPermissions,
} from '@/core/auth/permissions';

type PermissionGuardProps =
  | { required: Permission; requiredAny?: never; requiredAll?: never; children: ReactNode }
  | { required?: never; requiredAny: readonly Permission[]; requiredAll?: never; children: ReactNode }
  | { required?: never; requiredAny?: never; requiredAll: readonly Permission[]; children: ReactNode };

export function PermissionGuard(props: PermissionGuardProps) {
  const allowedSingle = useHasPermission(
    'required' in props && props.required ? props.required : ('product:read' as Permission),
  );
  const allowedAny = useHasAnyPermission(
    'requiredAny' in props && props.requiredAny ? props.requiredAny : [],
  );
  const allowedAll = useHasAllPermissions(
    'requiredAll' in props && props.requiredAll ? props.requiredAll : [],
  );

  let allowed = false;
  if ('required' in props && props.required) {
    allowed = allowedSingle;
  } else if ('requiredAny' in props && props.requiredAny) {
    allowed = allowedAny;
  } else if ('requiredAll' in props && props.requiredAll) {
    // useHasAllPermissions returns true on empty list when logged in;
    // requiredAll is required by the type, so empty arrays from
    // callers are still treated as "no requirement" → allowed.
    allowed = allowedAll;
  }

  if (!allowed) {
    return <PermissionDeniedState />;
  }
  return <>{props.children}</>;
}

/**
 * Inline denied surface. Intentionally NOT a redirect — auth is fine,
 * the user just lacks this specific capability. A "Back to dashboard"
 * link gives them an obvious recovery path.
 */
export function PermissionDeniedState() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  return (
    <SharedPermissionDeniedState
      data-testid="permission-denied-state"
      title={t('errors.forbidden.page.title', 'You don’t have access to this page')}
      description={t(
        'errors.forbidden.page.body',
        'Ask an administrator for access, or return to the dashboard.',
      )}
      primaryAction={
        <Button type="button" size="dense" variant="secondary" onClick={() => navigate(ROUTE_PATHS.HOME)}>
          {t('errors.forbidden.page.backHome', 'Back to dashboard')}
        </Button>
      }
    />
  );
}
